import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Box, Stack, SxProps } from '@mui/material';
import React, { Ref, useEffect, useImperativeHandle, useState } from 'react';
import { useInputModal } from 'services/modals/hooks';
import { FormField } from 'common/ui';
import {
  RulesetFormValues,
  SubmittedRulesetFormValues
} from 'modules/analyse/types/RulesetFormValues';
import { Button } from 'system/atoms';
import { actionsValidationSchema } from 'modules/analyse/constants/actionsValidationsSchema';
import { requirementsValidationSchema } from 'modules/analyse/constants/requirementsValidationSchema';
import { ToastType, useToast } from 'services/toast';
import { AutomationComponents } from 'common/ui/AutomationComponents';
import { removeKeyFromItems } from 'core/utils/reformatItemsWithKey';
import { Checkbox } from 'common/form';

type Props = {
  initialValues?: RulesetFormValues;
  showRulesetNameInput?: boolean;
  saveAsRulesetInitialValue?: boolean;
  hideTemplateCreateButton?: boolean;
  onFormSubmitted: (
    values: SubmittedRulesetFormValues,
    isSubmittedFromModal?: boolean
  ) => void;
  isVerticalForm?: boolean;
  isSubmitting?: boolean;
  onCanceled?: () => void;
  isEditable?: boolean;
  autofillDefaultTemplate?: boolean;
  templateId?: string;
  onTemplateChange?: (templateId: string) => void;
  formRef?: Ref<RulesetFormHandle>;
};

export type RulesetFormHandle = {
  clearRequirements: () => void;
};

const nextButtonSx: SxProps = {
  display: 'flex'
};

const rulesetValidationSchema = Yup.object().shape({
  groupName: Yup.string().required('Ruleset name is required'),
  analysisRequirements:
    requirementsValidationSchema.fields.analysisRequirements,
  actionAutomationTasks: actionsValidationSchema().fields.automationTasks
});

const rulesetValidationSchemaWithoutGroupName = Yup.object().shape({
  analysisRequirements:
    requirementsValidationSchema.fields.analysisRequirements,
  actionAutomationTasks: actionsValidationSchema().fields.automationTasks
});

export const RulesetForm: React.FC<Props> = ({
  initialValues,
  onFormSubmitted,
  isVerticalForm,
  showRulesetNameInput = true,
  hideTemplateCreateButton = false,
  saveAsRulesetInitialValue,
  isSubmitting: isSubmittingProp,
  onCanceled,
  templateId,
  onTemplateChange,
  autofillDefaultTemplate = false,
  formRef
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [enableReinitialize, setEnableReinitialize] = useState(true);
  const [saveAsRuleset, setSaveAsRuleset] = useState(false);

  const { showToast } = useToast();
  const { showModal } = useInputModal();

  const formik = useFormik<RulesetFormValues>({
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: !showRulesetNameInput
      ? rulesetValidationSchemaWithoutGroupName
      : rulesetValidationSchema,
    initialValues: initialValues || {
      groupName: '',
      analysisRequirements: [],
      actionAutomationTasks: [],
      ruleAutomationTasks: []
    },
    onSubmit: () => {
      // do nothing
    },
    enableReinitialize: true
  });

  useImperativeHandle(formRef, () => ({
    clearRequirements: () => formik.setFieldValue('analysisRequirements', [])
  }));

  useEffect(() => {
    if (saveAsRulesetInitialValue !== undefined) {
      setSaveAsRuleset(saveAsRulesetInitialValue);
    }
  }, [saveAsRulesetInitialValue]);

  const handleModalAndSubmit = async (
    values: RulesetFormValues,
    isSubmittedFromModal: boolean
  ) => {
    if (!values.analysisRequirements.length) {
      showToast({
        type: ToastType.Error,
        title: 'Enter a requirement'
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // If saving as ruleset but name is not provided, show modal
      if (saveAsRuleset) {
        showModal({
          title: "What's your ruleset called?",
          inputLabel: 'Ruleset name',
          onConfirmClick: async (value) => {
            await formik.setFieldValue('groupName', value);
            await handleSubmit(
              {
                ...values,
                groupName: value
              },
              true
            );
          }
        });
        return;
      }

      await handleSubmit(
        {
          ...values,
          groupName: values.groupName || undefined
        },
        isSubmittedFromModal
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (
    values: RulesetFormValues,
    isSubmittedFromModal: boolean
  ) => {
    if (!isSubmittedFromModal) {
      await formik.setTouched({
        groupName: true
      });
    }

    const errors = await formik.validateForm();

    if (Object.keys(errors).length === 0 || !errors) {
      const formattedValuesWithoutKeys: SubmittedRulesetFormValues = {
        ...values,
        analysisRequirements: removeKeyFromItems(values.analysisRequirements)
      };

      setEnableReinitialize(false);
      await onFormSubmitted(formattedValuesWithoutKeys, saveAsRuleset);
    } else {
      formik.setErrors(errors);
    }
  };

  return (
    <Box>
      <AutomationComponents
        formik={formik}
        isVerticalForm={isVerticalForm}
        actionsEnableReinitialize={enableReinitialize}
        hideTemplateCreateButton={hideTemplateCreateButton}
        autofillDefaultTemplate={autofillDefaultTemplate}
        templateId={templateId}
        onTemplateChange={onTemplateChange}
        include={{
          actions: true,
          rules: true,
          requirements: true,
          templates: true
        }}
      />

      {showRulesetNameInput && (
        <FormField
          isVertical={isVerticalForm}
          width={isVerticalForm ? '100%' : undefined}
          gap={isVerticalForm ? '40px' : undefined}
          title="Details"
          subTitle="Help others to identify and use this ruleset correctly"
          textFields={[
            {
              label: 'Ruleset name',
              props: {
                id: 'groupName',
                name: 'groupName',
                value: formik.values.groupName,
                onChange: (event) => {
                  formik.validateField('groupName');
                  formik.handleChange(event);
                },
                error: formik.touched.groupName && !!formik.errors.groupName,
                errorText: 'Ruleset name is required',
                sx: {
                  maxWidth: '50%'
                }
              }
            }
          ]}
          customComponents={[
            <Stack direction="row" gap={1}>
              <Button
                variant="primary"
                onClick={() => handleModalAndSubmit(formik.values, false)}
                disabled={isSubmitting || isSubmittingProp}
                loading={isSubmitting || isSubmittingProp}
              >
                Save ruleset
              </Button>

              {onCanceled && <Button onClick={onCanceled}>Cancel</Button>}
            </Stack>
          ]}
        />
      )}

      {!showRulesetNameInput && (
        <FormField
          isVertical={isVerticalForm}
          customComponents={[
            <Stack spacing={2} alignItems="flex-start">
              <Checkbox
                value={saveAsRuleset}
                onChange={(value) => setSaveAsRuleset(value)}
                field={{
                  checkboxLabel: 'Save this set up as a ruleset'
                }}
              />
              <Button
                sx={nextButtonSx}
                variant="primary"
                onClick={() => handleModalAndSubmit(formik.values, false)}
                disabled={isSubmitting || isSubmittingProp}
                loading={isSubmitting || isSubmittingProp}
              >
                Next
              </Button>
            </Stack>
          ]}
        />
      )}
    </Box>
  );
};
